// notificationContactsStore.js
import { defineStore } from 'pinia';
import axios from 'axios';
import { BACKEND_BASEURL } from '@/config/config';

export class NotificationContact {
  constructor(data) {
    this._id = data._id; // MongoDB _id for stable references
    this.nameFriendly = data.nameFriendly;
    this.nameFirst = data.nameFirst;
    this.nameLast = data.nameLast;
    this.titleBefore = data.titleBefore;
    this.titleAfter = data.titleAfter;
    this.salutation = data.salutation;
    this.emailAddress = data.emailAddress;
    this.phoneNumber = data.phoneNumber;
    this.description = data.description;
  }
}

/**
 * Notification contacts store
 * Uniquely identifies communities by ECID
 */
export const useNotificationContactsStore = defineStore('notificationContacts', {
  state: () => ({
    contacts: [],
  }),
  actions: {
    async fetchNotificationContacts(ecid) {
      try {
        // Fetch either all contacts or those for a specific community
        const url = ecid
          ? `${BACKEND_BASEURL}notificationContacts/${ecid}`
          : `${BACKEND_BASEURL}notificationContacts`;
        const response = await axios.get(url);
        this.contacts = response.data;
      } catch (error) {
        console.error('Failed to fetch notification contacts:', error);
      }
    },

    /**
     * Get a notification contact by its ID.
     * @param {string} ecid - The ECID of the community
     * @param {string} contactId - The MongoDB _id of the contact
     * @returns {Promise<Object|null>} The contact object or null if not found
     */
    async getNotificationContact(ecid, contactId) {
      try {
        const response = await axios.get(`${BACKEND_BASEURL}notificationContacts/${ecid}/${contactId}`);
        return response.data;
      } catch (error) {
        console.error('Failed to fetch notification contact:', error);
        return null;
      }
    },

    async createNotificationContact(ecid, newContact) {
      try {
        const response = await axios.post(`${BACKEND_BASEURL}notificationContacts/${ecid}`, newContact);
        this.contacts.push(response.data);
      } catch (error) {
        console.error('Failed to create notification contact:', error);
        throw error;
      }
    },

    /**
     * Update a notification contact by its ID.
     * @param {string} ecid - The ECID of the community
     * @param {string} contactId - The MongoDB _id of the contact
     * @param {Object} updatedContact - The updated contact data
     */
    async updateNotificationContact(ecid, contactId, updatedContact) {
      try {
        const response = await axios.put(`${BACKEND_BASEURL}notificationContacts/${ecid}/${contactId}`, updatedContact);
        // Find and update the contact in the store by _id
        const index = this.contacts.findIndex(c => c._id === contactId);
        if (index !== -1) {
          this.contacts[index] = response.data;
        }
      } catch (error) {
        console.error('Failed to update notification contact:', error);
        throw error;
      }
    },

    /**
     * Delete a notification contact by its ID.
     * @param {string} ecid - The ECID of the community
     * @param {string} contactId - The MongoDB _id of the contact
     */
    async deleteNotificationContact(ecid, contactId) {
      try {
        await axios.delete(`${BACKEND_BASEURL}notificationContacts/${ecid}/${contactId}`);
        // Find and remove the contact from the store by _id
        const index = this.contacts.findIndex(c => c._id === contactId);
        if (index !== -1) {
          this.contacts.splice(index, 1);
        }
      } catch (error) {
        console.error('Failed to delete notification contact:', error);
        throw error;
      }
    },
  },
}); 